import styled from 'styled-components';

import { FileDropzone } from '@components/file-dropzone';
import { UploadedFile } from '@components/file-field/uploaded-file';
import { IconFile } from '@components/icons/file';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { getScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { FieldDropzone } from './field-dropzone';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';

type Config = FieldConfig<'content-file'>;

const getDisplayName = (field: { fileAlias: string; fileName: string }) => {
  return field.fileAlias ?? field.fileName;
};

const Display: Config['Display'] = ({ field }) => {
  const showPreview = usePreviewFileModal();

  if (!field.url) {
    return <Fallback />;
  }

  const displayName = getDisplayName(field);

  return (
    <Stack space={1}>
      {field.label && <Text font="bold14" text={field.label} />}
      <UploadedFile
        name={displayName}
        path={field.url}
        onPreview={() =>
          showPreview({
            formFile: {
              name: displayName,
              src: field.url,
              srcConverted: field.urlConverted,
              originalFileName: field.fileName,
            },
          })
        }
      />
    </Stack>
  );
};

const FormBuilderContentFile: Config['Component'] = ({ field, dragHandle }) => {
  const showPreview = usePreviewFileModal();
  const {
    store: { onFileSave, actions: formActions },
  } = useFormBuilderContext();

  const setFileFields = (url: string, fileName: string, fileId: string, urlConverted?: string) => {
    formActions.createChangeHandler(field, 'fileName')(fileName);
    formActions.createChangeHandler(field, 'fileId')(fileId);
    formActions.createChangeHandler(field, 'fileAlias')(fileName);
    formActions.createChangeHandler(field, 'url')(url);
    if (urlConverted) {
      formActions.createChangeHandler(field, 'urlConverted')(urlConverted);
    }
  };

  const handleUpload = async (file: File, setProgress?: (progress: number) => void) => {
    const data = new FormData();
    data.append(field.id, file);
    let result;
    try {
      formActions.startSaving();
      result = await onFileSave?.({ data, setProgress });
    } finally {
      formActions.stopSaving();
    }
    if (!result) return;
    setFileFields(result.url, result.fileName, result.fileId, result.urlConverted);
  };

  const displayName = getDisplayName(field);

  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        display={<Display field={field} />}
        edit={
          <Stack space={4}>
            <TextField
              onChange={formActions.createChangeHandler(field, 'label')}
              defaultValue={field.label}
              label={t('label')}
              placeholder={t('FilePlaceholder')}
            />
            {field.url ? (
              <>
                <TextField
                  key={field.fileId}
                  onChange={(value) =>
                    formActions.createChangeHandler(field, 'fileAlias')(value || field.fileName)
                  }
                  defaultValue={displayName}
                  label={t('fileAlias')}
                />
                <UploadedFile
                  name={displayName}
                  path={field.url}
                  onPreview={() =>
                    showPreview({
                      formFile: {
                        name: displayName,
                        src: field.url,
                        srcConverted: field.urlConverted,
                        originalFileName: field.fileName,
                      },
                    })
                  }
                  onRemove={() => setFileFields('', '', '')}
                />
              </>
            ) : (
              <FileDropzone handleUpload={handleUpload} />
            )}
          </Stack>
        }
      />
    </FieldDropzone>
  );
};

const Fallback = () => (
  <FallbackContainer>
    <IconFile size={40} color="purple50" />
    <Text font="regular14" color="purple50" text={t('noFile')} />
  </FallbackContainer>
);

const { t } = getScopedTranslate({
  en: {
    title: 'File',
    noFile: 'No file added',
    label: 'Label',
    fileAlias: 'File display name',
    FilePlaceholder: 'File label...',
  },
});

export const contentFileConfig: Config = {
  kind: 'content-file',
  title: t('title'),
  group: 'content',
  Icon: IconFile,
  Component: FormBuilderContentFile,
  Display,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label || t('title')} />,
  propertiesTabs: [conditionsTab],
};

const FallbackContainer = styled.div({
  borderRadius: pxToRem(8),
  background: theme.color.uiTrans5,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: pxToRem(16),
});
